import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TableViewIcon from "@mui/icons-material/TableView";
import { alpha } from "@mui/material/styles";
import ForumIcon from "@mui/icons-material/Forum";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsightsIcon from "@mui/icons-material/Insights";

import { getMastodonResults, getMastodonTasks } from "../../services/mastodon_services";
import { getAllResultsCount } from "../../services/generic_services";
import KpiTile from "../results/KpiTile";
import FiltersPanel from "../results/FiltersPanel";

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows, columns) => {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(c.get(r))).join(","))
    .join("\n");
  return `﻿${header}\n${body}\n`;
};

const downloadBlob = (content, mime, filename) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const MASTODON_COLOR = "#6364FF";

const SORT_FIELDS = [
  { value: "created_at", label: "Date" },
  { value: "hashtag", label: "Hashtag" },
  { value: "instance", label: "Instance" },
];

const inDateRange = (dateLike, range) => {
  if (range === "all" || !dateLike) return true;
  const d = new Date(dateLike).getTime();
  if (Number.isNaN(d)) return true;
  const now = Date.now();
  const cutoffMs = { "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 }[range];
  return cutoffMs ? now - d <= cutoffMs : true;
};

const PostCard = ({ entry }) => {
  const post = entry.post || {};
  const confidencePct = typeof post.confidence === "number"
    ? Math.round(post.confidence * 100)
    : null;

  return (
    <Card
      sx={{
        p: 2,
        borderLeft: `3px solid ${MASTODON_COLOR}`,
        "&:hover": { boxShadow: `0 2px 12px ${alpha(MASTODON_COLOR, 0.15)}` },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            @{post.author || "unknown"}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              · {entry.hashtag?.includes("#") ? entry.hashtag : `#${entry.hashtag}`} · {entry.instance}
            </Typography>
          </Typography>
          {post.created_at && (
            <Typography variant="caption" color="text.disabled">
              {post.created_at}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Confidence percentage — hidden for now
          {confidencePct !== null && (
            <Chip
              size="small"
              label={`${confidencePct}%`}
              sx={{
                backgroundColor: confidencePct >= 50
                  ? alpha(MASTODON_COLOR, 0.18)
                  : alpha("#999", 0.15),
                color: confidencePct >= 50 ? MASTODON_COLOR : "text.secondary",
                fontWeight: 600,
              }}
            />
          )}
          */}
          {post.url && (
            <Tooltip title="Open on instance">
              <IconButton
                size="small"
                component="a"
                href={post.url}
                target="_blank"
                rel="noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
        {post.text}
      </Typography>

      {Array.isArray(post.tags) && post.tags.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {post.tags.map((t) => (
            <Chip key={t} label={`#${t}`} size="small" variant="outlined" />
          ))}
        </Box>
      )}
    </Card>
  );
};

const MastodonResults = () => {
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [allHashtags, setAllHashtags] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    dateRange: "all",
    confidenceMin: 0,
    sources: {},
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        sort_by: sortBy,
        sort_direction: sortDirection,
        search: filters.search,
      };
      // Only send the hashtags filter if the user has unchecked at least one
      const checked = allHashtags.filter((h) => filters.sources[h] !== false);
      if (allHashtags.length > 0 && checked.length < allHashtags.length) {
        params.hashtags = checked.join(",");
      }
      const [pageData, counts] = await Promise.allSettled([
        getMastodonResults(params),
        getAllResultsCount(),
      ]);
      if (pageData.status === "fulfilled") setResults(pageData.value || []);
      if (counts.status === "fulfilled") setTotalCount(counts.value?.mastodon || 0);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortDirection, filters.search, filters.sources, allHashtags]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch all tracked hashtags from tasks (one-time, gives complete list)
  useEffect(() => {
    (async () => {
      try {
        const tasks = await getMastodonTasks();
        const tags = Array.from(new Set((tasks || []).map((t) => t.hashtag).filter(Boolean))).sort();
        setAllHashtags(tags);
      } catch (e) {
        console.log("Failed to fetch Mastodon hashtags", e);
      }
    })();
  }, []);

  const hashtagSources = useMemo(
    () =>
      allHashtags.map((tag) => ({
        key: tag,
        label: tag.includes("#") ? tag : `#${tag}`,
        color: MASTODON_COLOR,
        checked: filters.sources[tag] !== false,
      })),
    [allHashtags, filters.sources]
  );

  const toggleSource = (key) => {
    setFilters((f) => ({
      ...f,
      sources: { ...f.sources, [key]: f.sources[key] === false },
    }));
    setPage(1);
  };

  const resetFilters = () =>
    setFilters({ search: "", dateRange: "all", confidenceMin: 0, sources: {} });

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return results.filter((entry) => {
      const post = entry.post || {};
      const tagOk = filters.sources[entry.hashtag] !== false;
      if (!tagOk) return false;
      if (!inDateRange(post.created_at, filters.dateRange)) return false;
      const conf = typeof post.confidence === "number" ? post.confidence : 0;
      if (conf < filters.confidenceMin) return false;
      if (q) {
        const hay = [post.text, post.author, entry.hashtag, entry.instance]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [results, filters]);

  const kpis = useMemo(() => {
    const inWindow = (range) =>
      results.filter((r) => inDateRange((r.post || {}).created_at, range)).length;
    const confidences = results
      .map((r) => (r.post || {}).confidence)
      .filter((c) => typeof c === "number");
    const avgConf = confidences.length
      ? confidences.reduce((a, b) => a + b, 0) / confidences.length
      : null;
    return {
      total: totalCount,
      last24h: inWindow("24h"),
      last7d: inWindow("7d"),
      avgConf,
    };
  }, [results, totalCount]);

  const [exportAnchor, setExportAnchor] = useState(null);

  const exportVisible = useCallback((format) => {
    const date = new Date().toISOString().slice(0, 10);
    const base = `mastodon-results-${date}`;
    if (format === "json") {
      downloadBlob(JSON.stringify(filtered, null, 2), "application/json", `${base}.json`);
    } else {
      const cols = [
        { label: "hashtag", get: (e) => e.hashtag },
        { label: "instance", get: (e) => e.instance },
        { label: "post_id", get: (e) => (e.post || {}).post_id },
        { label: "author", get: (e) => (e.post || {}).author },
        { label: "created_at", get: (e) => (e.post || {}).created_at },
        { label: "url", get: (e) => (e.post || {}).url },
        { label: "confidence", get: (e) => (e.post || {}).confidence },
        { label: "prediction", get: (e) => (e.post || {}).prediction },
        { label: "text", get: (e) => ((e.post || {}).text || "").replace(/\s+/g, " ") },
      ];
      downloadBlob(toCsv(filtered, cols), "text/csv;charset=utf-8", `${base}.csv`);
    }
    setExportAnchor(null);
  }, [filtered]);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/results" underline="hover" color="text.secondary">
          All Results
        </MuiLink>
        <Typography variant="body2" color="text.primary">Mastodon</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Mastodon Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount.toLocaleString()} posts collected
            {lastUpdated && (
              <Box component="span" sx={{ color: "text.disabled", ml: 1 }}>
                · updated {lastUpdated.toLocaleTimeString()}
              </Box>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value="results" exclusive size="small">
            <ToggleButton component={RouterLink} to="/mastodon/tasks" value="tasks">
              Tasks
            </ToggleButton>
            <ToggleButton component={RouterLink} to="/mastodon/results" value="results">
              Results
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            onClick={(e) => setExportAnchor(e.currentTarget)}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            endIcon={<ArrowDropDownIcon fontSize="small" />}
            disabled={filtered.length === 0}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => exportVisible("json")}>
              <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="JSON" secondary="Full structure" />
            </MenuItem>
            <MenuItem onClick={() => exportVisible("csv")}>
              <ListItemIcon><TableViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="CSV" secondary="Flat, Excel-ready" />
            </MenuItem>
          </Menu>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Total"
            value={kpis.total}
            unit="posts"
            accent={MASTODON_COLOR}
            icon={<ForumIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Last 24h"
            value={kpis.last24h}
            unit="posts"
            accent={MASTODON_COLOR}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Last 7d"
            value={kpis.last7d}
            unit="posts"
            accent={MASTODON_COLOR}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        {/* Avg confidence — hidden for now
        <Grid item xs={6} md={3}>
          <KpiTile
            label="Avg confidence"
            value={kpis.avgConf !== null ? `${Math.round(kpis.avgConf * 100)}%` : "—"}
            accent={MASTODON_COLOR}
            icon={<InsightsIcon fontSize="small" />}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        */}
      </Grid>

      {/* Filters + Activity feed */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FiltersPanel
            sources={hashtagSources}
            onSourceToggle={toggleSource}
            search={filters.search}
            onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
            dateRange={filters.dateRange}
            onDateRangeChange={(v) => setFilters((f) => ({ ...f, dateRange: v }))}
            confidenceMin={filters.confidenceMin}
            onConfidenceChange={(v) => setFilters((f) => ({ ...f, confidenceMin: v }))}
            onReset={resetFilters}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          {/* Sort controls */}
          <Card sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                >
                  {SORT_FIELDS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Direction</InputLabel>
                <Select
                  label="Direction"
                  value={sortDirection}
                  onChange={(e) => { setSortDirection(e.target.value); setPage(1); }}
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
                Page {page}
              </Typography>
            </Stack>
          </Card>

          {results.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No Mastodon posts yet. Start a hashtag crawl to see results here.
              </Typography>
            </Card>
          ) : filtered.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No items match the current filters.
              </Typography>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filtered.map((entry, idx) => (
                <PostCard
                  key={`${entry.hashtag_id || ""}-${(entry.post || {}).post_id || idx}`}
                  entry={entry}
                />
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={10}
              page={page}
              color="primary"
              onChange={(_, p) => setPage(p)}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MastodonResults;
